import { z } from "zod";
import { savedElementsSchema, savedLookSchema } from "../blocks/saved";
import type { BrandTokens } from "../brand/tokens";
import { ASSET_KINDS, STORAGE_CATEGORIES } from "../domain/media";
import { PAID_PLAN_IDS, PLAN_IDS, SUBSCRIPTION_STATUSES } from "../domain/plans";
import { ORG_ROLES } from "../domain/roles";
import type { SceneDocument } from "../scene/schema";

export const orgRoleSchema = z.enum(ORG_ROLES);

export const userRefSchema = z.object({ id: z.string(), name: z.string() }).nullable();

export const orgSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  logo: z.string().nullable(),
  role: orgRoleSchema,
  /** No running subscription: members can look, but not work (docs/16-geschaeftsmodell.md). */
  readOnly: z.boolean(),
});
export type OrgSummary = z.infer<typeof orgSummarySchema>;

export const orgDetailSchema = orgSummarySchema.extend({
  createdAt: z.date(),
  memberCount: z.number(),
  storageQuotaBytes: z.number(),
  storageUsedBytes: z.number(),
});
export type OrgDetail = z.infer<typeof orgDetailSchema>;

export const projectSchema = z.object({
  id: z.uuid(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  archivedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: userRefSchema,
  documentCount: z.number(),
  sizeBytes: z.number(),
  /** Brand kit of the project; null = the organization's default kit. */
  brandKitId: z.uuid().nullable(),
});
export type Project = z.infer<typeof projectSchema>;

export const documentSummarySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  title: z.string(),
  kind: z.enum(["design", "video"]),
  formatPreset: z.string(),
  width: z.number(),
  height: z.number(),
  revision: z.number(),
  /** Content JSON + files owned by this document (thumbnails, exports). */
  sizeBytes: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: userRefSchema,
  updatedBy: userRefSchema,
  thumbnailUrl: z.string().nullable(),
  /** Shown in the template gallery ("Aus Vorlage erstellen"). */
  isTemplate: z.boolean(),
  /** People who currently have the document open. */
  activeUsers: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Open comment threads. */
  openComments: z.number(),
});
export type DocumentSummary = z.infer<typeof documentSummarySchema>;

export const documentDetailSchema = documentSummarySchema.extend({
  projectName: z.string(),
  content: z.custom<SceneDocument>(),
});
export type DocumentDetail = z.infer<typeof documentDetailSchema>;

/** A rectangle in fractions (0–1) of an image or video frame. */
const fractionRectSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const assetSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid().nullable(),
  /** Folder in the media library; null = top level. */
  folderId: z.uuid().nullable(),
  kind: z.enum(ASSET_KINDS),
  name: z.string(),
  originalName: z.string(),
  mime: z.string(),
  status: z.enum(["processing", "ready", "failed"]),
  /** Size of the original file. */
  sizeBytes: z.number(),
  /** Original + all derived files (thumbnails, previews). */
  totalBytes: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  durationMs: z.number().nullable(),
  url: z.string(),
  thumbnailUrl: z.string().nullable(),
  /** Videos and audio: preview, filmstrip and waveform for the video editor. */
  media: z
    .object({
      status: z.enum(["processing", "ready", "failed"]),
      proxyUrl: z.string().nullable(),
      filmstripUrl: z.string().nullable(),
      filmstrip: z
        .object({
          frames: z.number(),
          /** Source time per frame (ms). */
          interval: z.number(),
          frameWidth: z.number(),
          frameHeight: z.number(),
        })
        .nullable(),
      /** JSON `{ rate, peaks }` with peaks 0–100. */
      waveformUrl: z.string().nullable(),
      hasAudio: z.boolean(),
      /** While processing: the running step and its progress (0–100). */
      step: z.enum(["audio", "proxy", "filmstrip"]).nullable().default(null),
      progress: z.number().nullable().default(null),
    })
    .nullable()
    .default(null),
  /**
   * Cut-out copy of another image (background removed, PNG with transparency);
   * null for everything else. While `status` is "processing" the server is
   * still removing the background.
   */
  cutout: z
    .object({
      /** The original image (may be in the trash or deleted by now). */
      sourceId: z.uuid(),
      /** While processing: 0–100 when known. */
      progress: z.number().nullable(),
      /** While processing: how long it has been running, in ms (server clock). */
      elapsedMs: z.number().nullable(),
      /** Expected duration of the whole run in ms (from earlier runs). */
      estimatedMs: z.number().nullable(),
    })
    .nullable()
    .default(null),
  /**
   * Videos: the person cut out of every frame ("Person freistellen",
   * video/cutout.ts); null until someone asked for it. The editor plays
   * `url`: picture and matte side by side in one H.264 video, so both are
   * always the same frame.
   */
  matte: z
    .object({
      status: z.enum(["processing", "ready", "failed"]),
      url: z.string().nullable(),
      /** Where picture and matte lie in the file (fractions of its size). */
      layout: z.object({ color: fractionRectSchema, alpha: fractionRectSchema }).nullable(),
      /** While processing: 0–100 when known, running time and expected total (ms). */
      progress: z.number().nullable(),
      elapsedMs: z.number().nullable(),
      estimatedMs: z.number().nullable(),
      error: z.string().nullable(),
    })
    .nullable()
    .default(null),
  /**
   * Mockup image rendered from another image (the device alone, transparent
   * PNG); null for everything else. While `status` is "processing" the
   * server is still rendering it.
   */
  mockup: z
    .object({
      /** The screenshot in the device (may be in the trash or deleted by now). */
      sourceId: z.uuid(),
      /** Name of the device model. */
      deviceName: z.string(),
    })
    .nullable()
    .default(null),
  error: z.string().nullable(),
  createdAt: z.date(),
  createdBy: userRefSchema,
});
export type Asset = z.infer<typeof assetSchema>;
export type AssetCutout = NonNullable<Asset["cutout"]>;
export type AssetMockup = NonNullable<Asset["mockup"]>;
export type AssetMedia = NonNullable<Asset["media"]>;
export type AssetMatte = NonNullable<Asset["matte"]>;

/** Folder of the media library. The API returns all folders flat; the tree comes from `parentId`. */
export const assetFolderSchema = z.object({
  id: z.uuid(),
  /** null = top level. */
  parentId: z.uuid().nullable(),
  name: z.string(),
  /** Media directly in this folder (without subfolders and trash), in total and per type. */
  assetCount: z.number(),
  kindCounts: z.record(z.enum(ASSET_KINDS), z.number()),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: userRefSchema,
});
export type AssetFolder = z.infer<typeof assetFolderSchema>;

/** A saved building block or colour look of the organization. */
export const libraryItemSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("element"),
    id: z.uuid(),
    name: z.string(),
    data: savedElementsSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    createdBy: userRefSchema,
  }),
  z.object({
    kind: z.literal("look"),
    id: z.uuid(),
    name: z.string(),
    data: savedLookSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    createdBy: userRefSchema,
  }),
]);
export type LibraryItem = z.infer<typeof libraryItemSchema>;

export const TRANSCRIPT_LANGUAGES = ["de", "en", "auto"] as const;
export type TranscriptLanguage = (typeof TRANSCRIPT_LANGUAGES)[number];

export const TRANSCRIPT_LANGUAGE_LABELS: Record<TranscriptLanguage, string> = {
  auto: "Automatisch",
  de: "Deutsch",
  en: "Englisch",
};

/** Transcript of a video in source time (docs/05-video-und-untertitel.md §4). */
export const transcriptSchema = z.object({
  assetId: z.uuid(),
  status: z.enum(["queued", "processing", "done", "failed"]),
  language: z.string(),
  detectedLanguage: z.string().nullable(),
  model: z.string().nullable(),
  progress: z.number(),
  /** Jobs waiting before this one (queued only). */
  queuePosition: z.number().nullable(),
  words: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
      start: z.number(),
      end: z.number(),
      confidence: z.number().optional(),
    }),
  ),
  error: z.string().nullable(),
  /** How long the running attempt has been running, in ms (processing only). */
  elapsedMs: z.number().nullable().default(null),
  /** Expected duration of a whole run in ms (from earlier runs of the model). */
  estimatedMs: z.number().nullable().default(null),
  updatedAt: z.date(),
});
export type TranscriptInfo = z.infer<typeof transcriptSchema>;

/**
 * Work the server does in the background for an organization: preparing
 * uploaded media, speech recognition and exports. Uploads run in the
 * browser and are merged in by the web app.
 */
export const activitySchema = z.object({
  /** Stable per task, e.g. `transcript:<assetId>`. */
  id: z.string(),
  /**
   * cutout: background removal, `assetId` is the new cut-out copy; matte:
   * the person cut out of the video `assetId`.
   */
  kind: z.enum(["media", "transcript", "export", "cutout", "matte"]),
  /** media: upload (metadata, thumbnail) | audio | proxy | filmstrip; export: the format. */
  step: z.string().nullable(),
  status: z.enum(["queued", "running", "done", "failed"]),
  /** Media or document name. */
  title: z.string(),
  assetId: z.uuid().nullable(),
  documentId: z.uuid().nullable(),
  /** 0–100 when known. */
  progress: z.number().nullable(),
  /** Jobs ahead in the queue (queued only). */
  queuePosition: z.number().nullable(),
  /** How long the task has been running, in ms (server clock). */
  elapsedMs: z.number().nullable(),
  /** Expected duration of the whole task in ms. */
  estimatedMs: z.number().nullable(),
  /** e.g. the speech recognition model. */
  detail: z.string().nullable(),
  error: z.string().nullable(),
  /** Finished exports: download. */
  url: z.string().nullable(),
  /** Transcripts: requested language (to start it again). */
  language: z.string().nullable(),
  /** Started by the current user (uploaded, requested). */
  mine: z.boolean(),
  updatedAt: z.date(),
});
export type Activity = z.infer<typeof activitySchema>;

export const storageUsageSchema = z.object({
  totalBytes: z.number(),
  quotaBytes: z.number(),
  trashBytes: z.number(),
  byCategory: z.array(
    z.object({ category: z.enum(STORAGE_CATEGORIES), bytes: z.number(), files: z.number() }),
  ),
  byUser: z.array(
    z.object({
      userId: z.string().nullable(),
      name: z.string(),
      email: z.string().nullable(),
      bytes: z.number(),
    }),
  ),
  byProject: z.array(
    z.object({ projectId: z.uuid().nullable(), name: z.string(), bytes: z.number() }),
  ),
  largestAssets: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      kind: z.enum(ASSET_KINDS),
      bytes: z.number(),
      createdBy: userRefSchema,
    }),
  ),
});
export type StorageUsage = z.infer<typeof storageUsageSchema>;

const limitSchema = z.number().nullable();

/** Plan of an organization with this month's usage (settings → Tarif). */
export const orgPlanSchema = z.object({
  plan: z.enum(PLAN_IDS),
  /** Calendar month (Europe/Berlin) the monthly limits refer to. */
  periodStart: z.date(),
  periodEnd: z.date(),
  limits: z.object({
    members: limitSchema,
    /** Effective storage limit (own quota of the organization or the plan's). */
    storageBytes: z.number(),
    documents: limitSchema,
    uploadBytes: limitSchema,
    exportPages: limitSchema,
    exportMaxSide: limitSchema,
    videoCutouts: limitSchema,
    captionMinutes: limitSchema,
    cutouts: limitSchema,
    brandKits: limitSchema,
    mcp: z.boolean(),
  }),
  /** The operator set an own storage quota that replaces the plan's. */
  storageOverride: z.boolean(),
  usage: z.object({
    members: z.number(),
    pendingInvitations: z.number(),
    storageBytes: z.number(),
    documents: z.number(),
    exportPages: z.number(),
    videoCutouts: z.number(),
    captionSeconds: z.number(),
    cutouts: z.number(),
    brandKits: z.number(),
  }),
  billing: z.object({
    /** Online payment is set up on this server. */
    enabled: z.boolean(),
    /** The plan needs a subscription ("intern" does not). */
    required: z.boolean(),
    /** No running subscription: look, but do not work. */
    readOnly: z.boolean(),
    /** Latest subscription; null = never booked. */
    status: z.enum(SUBSCRIPTION_STATUSES).nullable(),
    trialEnd: z.date().nullable(),
    /** End of the paid period (next invoice, unless cancelled). */
    periodEnd: z.date().nullable(),
    /** Cancelled: the subscription ends at this time. */
    endsAt: z.date().nullable(),
    /** A new subscription starts with the free trial (only the first one does). */
    trialAvailable: z.boolean(),
    /** The caller may book, change and cancel (owner). */
    canManage: z.boolean(),
  }),
});
export type OrgPlan = z.infer<typeof orgPlanSchema>;

/** Order summary confirmed with "Zahlungspflichtig bestellen", right before Stripe's checkout. */
export const billingConsentInputSchema = z.object({
  orgId: z.string().min(1),
  plan: z.enum(PAID_PLAN_IDS),
  /** Accepted the terms and took note of the withdrawal notice. */
  acceptTerms: z.literal(true, { error: "Bitte stimme den AGB zu" }),
  /** Asked to start right away (the trial) before the withdrawal period ends. */
  earlyStart: z.literal(true, { error: "Bitte bestätige den sofortigen Beginn" }),
});

/** Organizations the signed-in user can cancel on the cancellation page. */
export const cancellableSchema = z.object({
  orgId: z.string(),
  name: z.string(),
  plan: z.enum(PLAN_IDS),
  status: z.enum(SUBSCRIPTION_STATUSES),
  periodEnd: z.date().nullable(),
  /** Already cancelled: ends at this time. */
  endsAt: z.date().nullable(),
});
export type Cancellable = z.infer<typeof cancellableSchema>;

export const CANCELLATION_KINDS = ["ordinary", "extraordinary"] as const;

/** "Verträge hier kündigen" (§ 312k BGB): the form, with or without a session. */
export const cancellationInputSchema = z.object({
  name: z.string().trim().min(1, "Bitte gib deinen Namen an").max(200),
  email: z.email("Bitte gib die E-Mail-Adresse deines Kontos an").max(200),
  /** Signed in: the organization to cancel. */
  orgId: z.string().min(1).optional(),
  /** Without a session: which organization (subscription) is meant. */
  organizationName: z.string().trim().max(200).optional(),
  kind: z.enum(CANCELLATION_KINDS).default("ordinary"),
  /** Required for an extraordinary cancellation. */
  reason: z.string().trim().max(2000).optional(),
});
export type CancellationInput = z.infer<typeof cancellationInputSchema>;

/** Confirmation shown after "Jetzt kündigen" (and sent by email when SMTP is set up). */
export const cancellationReceiptSchema = z.object({
  id: z.uuid(),
  createdAt: z.date(),
  name: z.string(),
  email: z.string(),
  organizationName: z.string().nullable(),
  kind: z.enum(CANCELLATION_KINDS),
  /** cancelled: ends at `endsAt`; received: we process it by hand and reply by email. */
  status: z.enum(["cancelled", "received"]),
  endsAt: z.date().nullable(),
});
export type CancellationReceipt = z.infer<typeof cancellationReceiptSchema>;

/** Cancellation requests for the admin area. */
export const adminCancellationSchema = z.object({
  id: z.uuid(),
  createdAt: z.date(),
  name: z.string(),
  email: z.string(),
  organizationName: z.string().nullable(),
  organizationId: z.string().nullable(),
  kind: z.enum(CANCELLATION_KINDS),
  reason: z.string().nullable(),
  status: z.enum(["cancelled", "open", "none"]),
  endsAt: z.date().nullable(),
  handledAt: z.date().nullable(),
});
export type AdminCancellation = z.infer<typeof adminCancellationSchema>;

export const trashItemSchema = z.object({
  type: z.enum(["project", "document", "asset"]),
  id: z.uuid(),
  name: z.string(),
  deletedAt: z.date(),
  purgeAt: z.date(),
  sizeBytes: z.number(),
});
export type TrashItem = z.infer<typeof trashItemSchema>;

export const systemInfoSchema = z.object({
  env: z.enum(["development", "test", "production"]),
  version: z.string(),
  publicUrl: z.string(),
  mcpUrl: z.string(),
  signupEnabled: z.boolean(),
  emailVerificationRequired: z.boolean(),
  maxUploadBytes: z.number(),
  authProviders: z.array(z.enum(["google"])),
});
export type SystemInfo = z.infer<typeof systemInfoSchema>;

// --- Editor & exports (Phase 1) ---------------------------------------------

export const EXPORT_FORMATS = ["png", "jpg", "pdf-print", "pdf-digital", "mp4"] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  png: "PNG",
  jpg: "JPG",
  "pdf-print": "PDF für den Druck (CMYK)",
  "pdf-digital": "PDF (digital)",
  mp4: "MP4-Video",
};

/** Page numbers (0-based) to export; empty = all pages. */
const pagesSchema = z.array(z.number().int().min(0).max(99)).max(100).default([]);

export const imageExportOptionsSchema = z.object({
  pages: pagesSchema,
  /** Output pixels per document pixel. */
  scale: z.number().min(0.25).max(8).default(1),
  /** JPG only. */
  quality: z.number().int().min(40).max(100).default(90),
  /** PNG only: leave out the page background (mockups, stickers, logos). */
  transparent: z.boolean().optional(),
});

export const printExportOptionsSchema = z.object({
  pages: pagesSchema,
  provider: z.string().max(50),
  profile: z.string().max(50),
  bleedMm: z.number().min(0).max(30),
  pdfx: z.enum(["X-4", "X-3", "X-1a"]).default("X-4"),
  cropMarks: z.boolean().default(false),
  richBlack: z.boolean().default(false),
  overprintBlack: z.boolean().default(false),
});

export const digitalPdfExportOptionsSchema = z.object({ pages: pagesSchema });

/** MP4 (H.264 + AAC) of a video document, rendered on the server. */
export const videoExportOptionsSchema = z.object({
  /** Shorter side of the video (the other side follows the format, see `videoExportSize`). */
  resolution: z.enum(["1080p", "720p"]).default("1080p"),
  quality: z.enum(["standard", "high"]).default("standard"),
  /** Burn the captions into the picture. */
  captions: z.boolean().default(true),
});
export type VideoExportOptions = z.infer<typeof videoExportOptionsSchema>;

export const exportRequestSchema = z.discriminatedUnion("format", [
  z.object({ format: z.literal("png"), options: imageExportOptionsSchema }),
  z.object({ format: z.literal("jpg"), options: imageExportOptionsSchema }),
  z.object({ format: z.literal("pdf-print"), options: printExportOptionsSchema }),
  z.object({ format: z.literal("pdf-digital"), options: digitalPdfExportOptionsSchema }),
  z.object({ format: z.literal("mp4"), options: videoExportOptionsSchema }),
]);
export type ExportRequest = z.infer<typeof exportRequestSchema>;
export type PrintExportOptions = z.infer<typeof printExportOptionsSchema>;
export type ImageExportOptions = z.infer<typeof imageExportOptionsSchema>;

export const preflightIssueSchema = z.object({
  severity: z.enum(["error", "warning", "info"]),
  code: z.string(),
  message: z.string(),
  pageIndex: z.number().optional(),
  elementId: z.string().optional(),
});

export const printReportSchema = z.object({
  checks: z.array(z.object({ label: z.string(), status: z.enum(["ok", "warning", "error"]) })),
  issues: z.array(preflightIssueSchema),
  summary: z.object({
    profile: z.string(),
    pdfx: z.string(),
    bleedMm: z.number(),
    pages: z.number(),
    maxTac: z.number(),
    fonts: z.number(),
    images: z.number(),
  }),
});
export type PrintReport = z.infer<typeof printReportSchema>;

export const exportSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  revision: z.number(),
  format: z.enum(EXPORT_FORMATS),
  options: z.record(z.string(), z.unknown()),
  status: z.enum(["queued", "processing", "done", "failed"]),
  progress: z.number(),
  /** Jobs waiting before this one (queued exports only). */
  queuePosition: z.number().nullable(),
  report: printReportSchema.nullable(),
  url: z.string().nullable(),
  fileName: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  error: z.string().nullable(),
  createdAt: z.date(),
  finishedAt: z.date().nullable(),
  expiresAt: z.date(),
  requestedBy: userRefSchema,
});
export type ExportInfo = z.infer<typeof exportSchema>;

export const documentVersionSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  revision: z.number(),
  label: z.string().nullable(),
  kind: z.enum(["auto", "manual", "restore"]),
  sizeBytes: z.number(),
  createdAt: z.date(),
  createdBy: userRefSchema,
});
export type DocumentVersion = z.infer<typeof documentVersionSchema>;

export const presenceSchema = z.object({
  userId: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  mode: z.enum(["edit", "view"]),
  since: z.date(),
});
export type Presence = z.infer<typeof presenceSchema>;

export const colorReportSchema = z.object({
  color: z.string(),
  cmyk: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  proof: z.string(),
  deltaE: z.number(),
  outOfGamut: z.boolean(),
  source: z.enum(["profile", "swatch", "black", "gray"]),
});
export type ColorReport = z.infer<typeof colorReportSchema>;

// --- Brand kit & fonts (Phase 2) --------------------------------------------

/** Media referenced by a brand kit (logos, graphics). */
export const brandAssetSchema = z.object({
  id: z.uuid(),
  kind: z.enum(ASSET_KINDS),
  name: z.string(),
  url: z.string(),
  thumbnailUrl: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});
export type BrandAsset = z.infer<typeof brandAssetSchema>;

export const brandKitSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isDefault: z.boolean(),
  /** A few colours for list previews. */
  previewColors: z.array(z.string()),
  projectCount: z.number(),
  updatedAt: z.date(),
});
export type BrandKitSummary = z.infer<typeof brandKitSummarySchema>;

export const brandKitSchema = brandKitSummarySchema.extend({
  tokens: z.custom<BrandTokens>(),
  logos: z.array(
    z.object({
      variant: z.enum(["primary", "light", "dark", "icon"]),
      asset: brandAssetSchema,
    }),
  ),
  graphics: z.array(brandAssetSchema),
  createdAt: z.date(),
  updatedBy: userRefSchema,
});
export type BrandKit = z.infer<typeof brandKitSchema>;

export const fontFaceSchema = z.object({
  id: z.uuid(),
  weight: z.number(),
  style: z.enum(["normal", "italic"]),
  url: z.string(),
  format: z.enum(["woff2", "woff", "truetype", "opentype"]),
  unicodeRange: z.string().nullable(),
});
export type FontFace = z.infer<typeof fontFaceSchema>;

export const fontSchema = z.object({
  id: z.uuid(),
  family: z.string(),
  source: z.enum(["upload", "google"]),
  category: z.string().nullable(),
  faces: z.array(fontFaceSchema),
  sizeBytes: z.number(),
  createdAt: z.date(),
  createdBy: userRefSchema,
});
export type OrgFont = z.infer<typeof fontSchema>;

/** Entry of the Google Fonts catalogue (via Fontsource), for importing. */
export const googleFontSchema = z.object({
  id: z.string(),
  family: z.string(),
  category: z.string(),
  weights: z.array(z.number()),
  styles: z.array(z.string()),
});
export type GoogleFont = z.infer<typeof googleFontSchema>;

/** Search across files, projects, media, own templates and saved blocks (⌘K). */
export const searchResultsSchema = z.object({
  documents: z.array(
    documentSummarySchema.extend({
      projectName: z.string(),
      /** Matching text inside the file (null: the title matched). */
      snippet: z.string().nullable(),
    }),
  ),
  templates: z.array(
    documentSummarySchema.extend({ projectName: z.string(), snippet: z.string().nullable() }),
  ),
  projects: z.array(projectSchema),
  media: z.array(assetSchema),
  /** Saved building blocks ("Meine Bausteine"); the editor has their content. */
  blocks: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type SearchResults = z.infer<typeof searchResultsSchema>;
